import * as yup from "yup"
import { regex } from "utils/FormSchema"

/**
 * Shared validation + copy for every Egged Plus form block (Contact Us,
 * Driver Career, General Career, Accident Report). The error strings are the
 * exact microcopy specified in the EGDE form tickets — keep this the single
 * source of truth so all forms stay consistent.
 */

export type FormLanguage = "en" | "he" | "ar" | "ru"

export interface FormFieldMessages {
  required: string
  invalid: string
  fullNameRequired: string
  phoneRequired: string
  phoneInvalid: string
  emailRequired: string
  emailInvalid: string
  timeFormat: string
  plateInvalid: string
  checkboxRequired: string
  fileRequired: string
  fileTypeDocs: string
  fileTypeImages: string
  photosMin: string
  photosMax: string
}

const he: FormFieldMessages = {
  required: "לא נוכל להתקדם בלי זה",
  invalid: "משהו לא מסתדר כאן. שננסה שוב?",
  fullNameRequired: "לא נוכל להתקדם בלי זה",
  phoneRequired: "איך נדע לאן לחזור?",
  phoneInvalid: "משהו לא מסתדר כאן. שננסה שוב?",
  emailRequired: "גם לכתובת מייל אנחנו זקוקים",
  emailInvalid: "משהו לא מסתדר כאן. שננסה שוב?",
  timeFormat: "נרצה פורמט כזה: HH:mm",
  plateInvalid: "משהו לא מסתדר כאן. שננסה שוב?",
  checkboxRequired: "רגע, שכחת לסמן כאן!",
  fileRequired: "לא נוכל להתקדם בלי זה",
  fileTypeDocs:
    "אפשר להעלות קבצים רק מסוג PDF, DOC, DOCX ובמשקל של עד 5 MB",
  fileTypeImages:
    "אפשר להעלות קבצים רק מסוג JPEG, JPG, PNG ובמשקל של עד 5 MB",
  // ${max} is interpolated by yup from the field's .max() value.
  photosMin: "צריך לצרף לפחות תמונה אחת",
  photosMax: "אפשר לצרף עד ${max} תמונות",
}

const en: FormFieldMessages = {
  required: "This field is required",
  invalid: "Something doesn't seem right. Try again?",
  fullNameRequired: "This field is required",
  phoneRequired: "How will we know where to reach you?",
  phoneInvalid: "Something doesn't seem right. Try again?",
  emailRequired: "We need an email address too",
  emailInvalid: "Something doesn't seem right. Try again?",
  timeFormat: "We'd like this format: HH:mm",
  plateInvalid: "Something doesn't seem right. Try again?",
  checkboxRequired: "Wait, you forgot to check here!",
  fileRequired: "This field is required",
  fileTypeDocs: "Only PDF, DOC, DOCX files up to 5 MB are allowed",
  fileTypeImages: "Only JPEG, JPG, PNG files up to 5 MB are allowed",
  photosMin: "Please attach at least one photo",
  photosMax: "You can attach up to ${max} photos",
}

export const formFieldMessages: Record<FormLanguage, FormFieldMessages> = {
  he,
  en,
  ar: en,
  ru: en,
}

export function getFieldMessages(locale: string): FormFieldMessages {
  return formFieldMessages[(locale as FormLanguage) ?? "he"] ?? he
}

/* ------------------------------------------------------------------ */
/* Shell chrome labels (success / error cards, submit button, etc.)     */
/* ------------------------------------------------------------------ */

export interface ShellLabels {
  requiredFields: string
  submit: string
  successTitle: string
  successMessage: string
  errorTitle: string
  errorMessage: string
  retry: string
}

/* EGDE-448: success / failure copy matches the Figma result cards (QA return
   2026-07-08 — the success body includes the full second sentence).
   EGDE-672: the client shortened the success copy to a two-line thank-you on
   every form — the old body pointed at vehicle types / offers that this site
   does not have. */
const shellHe: ShellLabels = {
  requiredFields: "שדות חובה",
  submit: "שליחה",
  successTitle: "תודה, קיבלנו!",
  successMessage: "נחזור אלייך בהקדם",
  errorTitle: "משהו השתבש בדרך...",
  errorMessage: "כנראה שהיתה שגיאה בשליחת הטופס. מזמינים אותך לנסות שוב",
  retry: "ניסיון נוסף",
}

const shellEn: ShellLabels = {
  requiredFields: "Required fields",
  submit: "Submit",
  successTitle: "Thanks, we got it!",
  successMessage: "We'll get back to you shortly",
  errorTitle: "Something went wrong...",
  errorMessage:
    "There seems to have been an error sending the form. Feel free to try again",
  retry: "Try again",
}

export function getShellLabels(locale: string): ShellLabels {
  return locale === "en" ? shellEn : shellHe
}

/* ------------------------------------------------------------------ */
/* Reusable yup field builders — compose into each form's schema        */
/* ------------------------------------------------------------------ */

export const fullNameField = (m: FormFieldMessages) =>
  yup
    .string()
    .required(m.fullNameRequired)
    .min(5, m.invalid)
    .matches(regex.singleName, m.invalid)

/** Israeli mobile (05X-XXXXXXX) — matches the shipped Contact form. */
export const phoneField = (m: FormFieldMessages) =>
  yup.string().required(m.phoneRequired).matches(regex.phoneNumber, m.phoneInvalid)

export const requiredEmailField = (m: FormFieldMessages) =>
  yup.string().required(m.emailRequired).matches(regex.email, m.emailInvalid)

/** Optional email — only validated when the user typed something. */
export const optionalEmailField = (m: FormFieldMessages) =>
  yup
    .string()
    .optional()
    .default("")
    .test("email-format", m.emailInvalid, (value) =>
      !value ? true : regex.email.test(value),
    )

export const requiredTextField = (m: FormFieldMessages) =>
  yup.string().required(m.required)

export const optionalTextField = () => yup.string().optional().default("")

/** Israeli vehicle plate — 7–8 digits, dashes stripped before testing. */
export const licensePlateField = (m: FormFieldMessages) =>
  yup
    .string()
    .transform((value) => (value ? String(value).replace(/-/g, "") : value))
    .required(m.required)
    .matches(regex.licensePlate, m.plateInvalid)

export const timeField = (m: FormFieldMessages) =>
  yup.string().required(m.required).matches(regex.time, m.timeFormat)

export const requiredDateField = (m: FormFieldMessages) =>
  yup
    .date()
    .typeError(m.required)
    .required(m.required)

/** Conditionally-required CMS checkbox. */
export const checkboxField = (m: FormFieldMessages, required: boolean) =>
  required
    ? yup.boolean().oneOf([true], m.checkboxRequired).required(m.checkboxRequired)
    : yup.boolean().default(false)

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

export const DOC_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]

export const IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"]

/** Required single-file upload (CV / license photo). */
export const requiredFileField = (
  m: FormFieldMessages,
  acceptedTypes: string[],
  typeMessage: string,
) =>
  yup
    .mixed<File>()
    .required(m.fileRequired)
    .test("fileSize", typeMessage, (value) =>
      !value ? true : (value as File).size <= MAX_FILE_SIZE,
    )
    .test("fileType", typeMessage, (value) =>
      !value ? true : acceptedTypes.includes((value as File).type),
    )

/** Optional multi-file upload (damage photos: 1–6 enforced by the field UI). */
export const optionalFilesField = (
  acceptedTypes: string[],
  typeMessage: string,
  maxFiles: number,
) =>
  yup
    .array()
    .of(yup.mixed<File>().required())
    .default([])
    .max(maxFiles, typeMessage)
    .test("filesSize", typeMessage, (files) =>
      !files ? true : files.every((f) => (f as File).size <= MAX_FILE_SIZE),
    )
    .test("filesType", typeMessage, (files) =>
      !files
        ? true
        : files.every((f) => acceptedTypes.includes((f as File).type)),
    )

/** Required multi-file upload (min 1, max N) — e.g. accident damage photos. */
export const requiredFilesField = (
  m: FormFieldMessages,
  acceptedTypes: string[],
  typeMessage: string,
  maxFiles: number,
) =>
  yup
    .array()
    .of(yup.mixed<File>().required())
    .default([])
    // EGDE-239: explicit min/max count messages (min was the generic required
    // string; max wrongly reused the file-type string). ${max} is interpolated.
    .min(1, m.photosMin)
    .max(maxFiles, m.photosMax)
    .test("filesSize", typeMessage, (files) =>
      !files ? true : files.every((f) => (f as File).size <= MAX_FILE_SIZE),
    )
    .test("filesType", typeMessage, (files) =>
      !files
        ? true
        : files.every((f) => acceptedTypes.includes((f as File).type)),
    )
